import axios from 'axios';
import { playSuccessSound } from '../utils/sounds';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5030/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Request interceptor — attach JWT, tenant ID, and custom domain header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    // Multipart registration fields are unavailable to backend middleware
    // until Multer runs. These headers distinguish students on a shared IP.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const organizationCode = config.data.get('organizationCode');
      const phone = config.data.get('phone');
      if (organizationCode) config.headers['X-Registration-Organization'] = String(organizationCode);
      if (phone) config.headers['X-Registration-Phone'] = String(phone);
    }
    // Auto-attach hostname so backend can resolve tenant from custom domain / subdomain
    const hostname = window.location.hostname;
    if (hostname && hostname !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      config.headers['X-Tenant-Domain'] = hostname;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 auto-logout + play success sound on mutations
api.interceptors.response.use(
  (response) => {
    // Soft confirmation ding for any successful write action (POST/PUT/PATCH/DELETE).
    // Excludes notification mark-as-read and polling endpoints so they stay silent.
    const method = response.config?.method?.toLowerCase();
    const url    = response.config?.url || '';
    const silentPaths = ['/notifications/', '/auth/', '/clock-status', '/unread-count'];
    if (
      ['post', 'put', 'patch', 'delete'].includes(method) &&
      response.data?.success !== false &&
      !silentPaths.some(p => url.includes(p))
    ) {
      playSuccessSound().catch(() => {});
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const isSA = user?.userType === 'super_admin';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenant');
      localStorage.removeItem('role');
      localStorage.removeItem('activeModules');
      window.location.href = isSA ? '/sa/login' : '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const getData = async (url, params = {}) => {
  const res = await api.get(url, { params });
  return res.data;
};

export const postData = async (url, data = {}) => {
  const res = await api.post(url, data);
  return res.data;
};

export const putData = async (url, data = {}) => {
  const res = await api.put(url, data);
  return res.data;
};

export const patchData = async (url, data = {}) => {
  const res = await api.patch(url, data);
  return res.data;
};

export const deleteData = async (url) => {
  const res = await api.delete(url);
  return res.data;
};

export const uploadFile = async (url, formData, onProgress) => {
  const res = await api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Uploads may take several minutes on mobile networks. Normal API calls
    // retain the shorter client timeout configured above.
    timeout: 30 * 60 * 1000,
    onUploadProgress: onProgress ? (e) => {
      if (e.total) onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
    } : undefined,
  });
  return res.data;
};
